import { useMemo, useState } from "react";

import useDailyReadStore from "@app/analytics/stores/useDailyReadStore";
import useIsomorphicLayoutEffect from "@app/common/hooks/useIsomorphicLayoutEffect";

export type Rect = Pick<
  DOMRectReadOnly,
  "x" | "y" | "top" | "left" | "right" | "bottom" | "height" | "width"
>;

const DEFAULT_STATE: Rect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

const useDailyReadPlayerDimensions = (): Rect => {
  const isInitialized = useDailyReadStore((state) => Boolean(state.id));
  const element = document.getElementById("daily-read-player-dialog");
  const [rect, setRect] = useState<Rect>(DEFAULT_STATE);

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        if (entries[0]) {
          const { x, y, width, height, top, left, bottom, right } =
            entries[0].contentRect;
          setRect({ x, y, width, height, top, left, bottom, right });
        }
      }),
    []
  );

  useIsomorphicLayoutEffect(() => {
    if (!element) return;
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [element]);

  if (!isInitialized) return DEFAULT_STATE;

  if (rect === DEFAULT_STATE) {
    // Return client height and width as soon as possible
    return {
      ...DEFAULT_STATE,
      width: element?.clientWidth ?? 0,
      height: element?.clientHeight ?? 0,
    };
  }

  return rect;
};

export default useDailyReadPlayerDimensions;
